import { TrendingUp, Receipt, Megaphone, ClipboardCheck } from 'lucide-react'
import { Card } from '../ui/Card'
import { CardSkeleton } from '../ui/Skeleton'
import type { RevenueSummaryItem } from '../../lib/api'
import { formatDoge, formatNumber } from '../../lib/utils'

interface SummaryCardsProps {
  data: RevenueSummaryItem[] | null
  loading: boolean
}

export default function SummaryCards({ data, loading }: SummaryCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <EmptyCard title="Total Revenue" icon={<TrendingUp />} />
        <EmptyCard title="Campaign Fees" icon={<Megaphone />} />
        <EmptyCard title="Survey Commissions" icon={<ClipboardCheck />} />
        <EmptyCard title="Total Transactions" icon={<Receipt />} />
      </div>
    )
  }

  const totalRevenue = data.reduce((sum, item) => sum + item.total_revenue, 0)
  const totalTransactions = data.reduce((sum, item) => sum + item.total_transactions, 0)

  const campaignFees = data.find((item) => item.revenue_type === 'campaign_fee')
  const surveyCommissions = data.find((item) => item.revenue_type === 'survey_commission')

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        title="Total Revenue"
        value={`${formatDoge(totalRevenue)} DOGE`}
        subtitle={`${formatNumber(totalTransactions)} transactions`}
        icon={<TrendingUp className="text-doge-400" />}
        highlight
      />
      <MetricCard
        title="Campaign Fees"
        value={`${formatDoge(campaignFees?.total_revenue ?? 0)} DOGE`}
        subtitle={`${formatNumber(campaignFees?.total_transactions ?? 0)} campaigns`}
        icon={<Megaphone className="text-blue-400" />}
      />
      <MetricCard
        title="Survey Commissions"
        value={`${formatDoge(surveyCommissions?.total_revenue ?? 0)} DOGE`}
        subtitle={`${formatNumber(surveyCommissions?.total_transactions ?? 0)} surveys`}
        icon={<ClipboardCheck className="text-green-400" />}
      />
      <MetricCard
        title="Total Transactions"
        value={formatNumber(totalTransactions)}
        subtitle={`${data.length} revenue types`}
        icon={<Receipt className="text-purple-400" />}
      />
    </div>
  )
}

interface MetricCardProps {
  title: string
  value: string
  subtitle: string
  icon: React.ReactNode
  highlight?: boolean
}

function MetricCard({ title, value, subtitle, icon, highlight }: MetricCardProps) {
  return (
    <Card className={highlight ? 'border-doge-500/30 bg-doge-500/5' : ''}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${highlight ? 'text-doge-400' : 'text-white'}`}>
            {value}
          </p>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className="p-2 bg-slate-700/50 rounded-lg">
          {icon}
        </div>
      </div>
    </Card>
  )
}

interface EmptyCardProps {
  title: string
  icon: React.ReactNode
}

function EmptyCard({ title, icon }: EmptyCardProps) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-600">--</p>
          <p className="text-xs text-slate-500 mt-1">No data</p>
        </div>
        <div className="p-2 bg-slate-700/50 rounded-lg text-slate-500">
          {icon}
        </div>
      </div>
    </Card>
  )
}
